// src/components/dashboard/ActivityCard.js
import React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Avatar,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import GroupsIcon from "@mui/icons-material/Groups";
import CommentIcon from "@mui/icons-material/Comment";
import useDivesiteStats from "../../hooks/useDives.js";

// Main glassmorphic activity card container
const GlassActivityCard = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  height: "100%",
  display: "flex",
  flexDirection: "column",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "2px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.6)}, 
      ${alpha(theme.palette.secondary.main, 0.4)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 25% 25%, 
      ${alpha(theme.palette.primary.main, 0.04)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 75% 75%, 
      ${alpha(theme.palette.secondary.main, 0.04)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  // Mobile-specific hover effects
  [theme.breakpoints.down("md")]: {
    borderRadius: 16,
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.1)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.85)} 0%, 
        ${alpha(theme.palette.background.paper, 0.65)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
  },

  // Desktop hover effects
  [theme.breakpoints.up("md")]: {
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.12)}`,
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.9)} 0%, 
        ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.25),
      "&::before": {
        background: `linear-gradient(90deg, 
          transparent, 
          ${alpha(theme.palette.primary.main, 0.8)}, 
          ${alpha(theme.palette.secondary.main, 0.6)}, 
          transparent)`,
      },
      "&::after": {
        background: `radial-gradient(circle at 25% 25%, 
          ${alpha(theme.palette.primary.main, 0.06)} 0%, 
          transparent 50%), 
          radial-gradient(circle at 75% 75%, 
          ${alpha(theme.palette.secondary.main, 0.06)} 0%, 
          transparent 50%)`,
      },
    },
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: 12,
  },
}));

// Glassmorphic title with gradient text
const GlassTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main} 0%, 
    ${theme.palette.secondary.main} 70%, 
    ${theme.palette.primary.main} 100%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  textShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  letterSpacing: "-0.01em",
  lineHeight: 1.3,
  wordBreak: "break-word",
  hyphens: "auto",
}));

// Glassmorphic chip with enhanced styling
const GlassChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, 0.12)} 0%, 
    ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  color: theme.palette.primary.main,
  fontWeight: 600,
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.3)}, 
      transparent)`,
    borderRadius: "inherit",
  },

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.18)} 0%, 
      ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.2)}`,
  },

  "& .MuiChip-icon": {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.primary.main, 0.3)})`,
  },
}));

// Glassmorphic divider
const GlassDivider = styled(Divider)(({ theme }) => ({
  background: `linear-gradient(90deg, 
    transparent 0%, 
    ${alpha(theme.palette.divider, 0.6)} 50%, 
    transparent 100%)`,
  height: "1px",
  position: "relative",
  margin: `${theme.spacing(2)} 0`,

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "10%",
    right: "10%",
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent 0%, 
      ${alpha(theme.palette.common.white, 0.15)} 50%, 
      transparent 100%)`,
  },
}));

// Glassmorphic comment paper
const GlassCommentPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.default, 0.6)} 0%, 
    ${alpha(theme.palette.background.default, 0.4)} 100%)`,
  backdropFilter: "blur(15px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  borderRadius: 16,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.04)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.common.white, 0.2)}, 
      transparent)`,
    borderRadius: "16px 16px 0 0",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 30%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 70%)`,
    pointerEvents: "none",
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

// Glassmorphic avatar
const GlassAvatar = styled(Avatar)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${theme.palette.primary.main} 0%, 
    ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
  border: `2px solid ${alpha(theme.palette.common.white, 0.2)}`,
  boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.common.white, 0.2)} 0%, 
      transparent 50%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },
}));

// Glassmorphic loading container
const GlassLoadingContainer = styled(Box)(({ theme }) => ({
  background: `radial-gradient(circle, 
    ${alpha(theme.palette.primary.main, 0.05)} 0%, 
    transparent 70%)`,
  borderRadius: 16,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "60px",
    height: "60px",
    background: `conic-gradient(${alpha(
      theme.palette.primary.main,
      0.1
    )}, transparent)`,
    borderRadius: "50%",
    animation: "glassSpin 2s linear infinite",
  },

  "@keyframes glassSpin": {
    "0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
    "100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
  },
}));

// Glassmorphic empty state container
const GlassEmptyState = styled(Box)(({ theme }) => ({
  background: `radial-gradient(circle, 
    ${alpha(theme.palette.action.disabled, 0.03)} 0%, 
    transparent 70%)`,
  borderRadius: 16,
  border: `1px dashed ${alpha(theme.palette.action.disabled, 0.2)}`,
  position: "relative",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(45deg, 
      transparent 30%, 
      ${alpha(theme.palette.action.disabled, 0.02)} 50%, 
      transparent 70%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

const ActivityCard = ({ divesite }) => {
  const { statsWithComments, loading, error } = useDivesiteStats(divesite._id);
  console.log("ActivityCard - statsWithComments:", statsWithComments);

  // Mobile-friendly responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Responsive title variant
  const getTitleVariant = () => {
    if (isMobile) return "subtitle1";
    return "h6";
  };

  // Responsive comment truncation
  const getCommentLength = () => {
    if (isMobile) return 60;
    if (isTablet) return 70;
    return 80;
  };

  return (
    <GlassActivityCard
      elevation={0}
      sx={{
        padding: isMobile ? theme.spacing(2.5) : theme.spacing(3),
        width: "100%",
        maxWidth: isMobile ? "100%" : "400px",
        minHeight: isMobile ? "auto" : "280px",
      }}
    >
      <GlassTitle
        variant={getTitleVariant()}
        component="h3"
        gutterBottom
        sx={{
          fontSize: isMobile ? "1.1rem" : "1.25rem",
          mb: isMobile ? 1.5 : 2,
        }}
      >
        {divesite.uddf.divesite.site.name}
      </GlassTitle>

      {loading ? (
        <GlassLoadingContainer
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: isMobile ? 3 : 4,
            minHeight: isMobile ? "120px" : "140px",
          }}
        >
          <CircularProgress
            size={isMobile ? 32 : 24}
            sx={{
              color: theme.palette.primary.main,
              filter: `drop-shadow(0 2px 8px ${alpha(
                theme.palette.primary.main,
                0.3
              )})`,
            }}
          />
        </GlassLoadingContainer>
      ) : error ? (
        <GlassEmptyState
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: isMobile ? 3 : 4,
            minHeight: isMobile ? "120px" : "140px",
          }}
        >
          <Typography
            color="error"
            variant={isMobile ? "body2" : "body1"}
            textAlign="center"
            sx={{
              fontWeight: 500,
              textShadow: `0 2px 4px ${alpha(theme.palette.error.main, 0.2)}`,
            }}
          >
            Error loading activity data
          </Typography>
        </GlassEmptyState>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: isMobile ? "center" : "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: isMobile ? 1 : 0,
            }}
          >
            <GlassChip
              icon={
                <GroupsIcon sx={{ fontSize: isMobile ? "1rem" : "1.2rem" }} />
              }
              label={`${statsWithComments?.totalDives || 0} ${
                isMobile ? "dives" : "dives logged"
              }`}
              size={isMobile ? "medium" : "small"}
              clickable={false}
              onClick={() => {}}
              sx={{
                fontSize: isMobile ? "0.875rem" : "0.8125rem",
                height: isMobile ? "36px" : "32px",
                "& .MuiChip-label": {
                  px: isMobile ? 1.5 : 1,
                  fontWeight: 600,
                },
                cursor: "default",
              }}
            />
          </Box>

          <GlassDivider />

          {statsWithComments?.latestComments?.length > 0 ? (
            <Box sx={{ mt: isMobile ? 1.5 : 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: isMobile ? 1 : 1.5,
                  gap: 0.5,
                }}
              >
                <CommentIcon
                  sx={{
                    fontSize: isMobile ? "1rem" : "1.2rem",
                    color: theme.palette.primary.main,
                    filter: `drop-shadow(0 2px 4px ${alpha(
                      theme.palette.primary.main,
                      0.3
                    )})`,
                  }}
                />
                <Typography
                  variant={isMobile ? "body2" : "subtitle2"}
                  fontWeight="bold"
                  sx={{
                    fontSize: isMobile ? "0.875rem" : "0.9375rem",
                    color: alpha(theme.palette.text.primary, 0.9),
                  }}
                >
                  Latest Comment:
                </Typography>
              </Box>

              <GlassCommentPaper
                sx={{
                  p: isMobile ? 1.5 : 1.5,
                  mt: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: isMobile ? "0.875rem" : "0.875rem",
                    lineHeight: 1.4,
                    fontStyle: "italic",
                    color: alpha(theme.palette.text.secondary, 0.9),
                  }}
                >
                  "
                  {statsWithComments.latestComments[0].comment.substring(
                    0,
                    getCommentLength()
                  )}
                  {statsWithComments.latestComments[0].comment.length >
                  getCommentLength()
                    ? "..."
                    : ""}
                  "
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: isMobile ? 1.5 : 1,
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 0.5 : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GlassAvatar
                      sx={{
                        width: isMobile ? 20 : 18,
                        height: isMobile ? 20 : 18,
                        fontSize: isMobile ? "0.75rem" : "0.6875rem",
                      }}
                    >
                      {(statsWithComments.latestComments[0]?.diverName || "A")
                        .charAt(0)
                        .toUpperCase()}
                    </GlassAvatar>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: isMobile ? "0.75rem" : "0.6875rem",
                        fontWeight: 600,
                        color: alpha(theme.palette.text.primary, 0.9),
                      }}
                    >
                      {statsWithComments.latestComments[0]?.diverName ||
                        "Anonymous"}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: isMobile ? "0.75rem" : "0.6875rem",
                      color: alpha(theme.palette.text.secondary, 0.8),
                      textAlign: isMobile ? "center" : "right",
                    }}
                  >
                    {new Date(
                      statsWithComments.latestComments[0].timestamp
                    ).toLocaleDateString(
                      undefined,
                      isMobile
                        ? { month: "short", day: "numeric" }
                        : { month: "short", day: "numeric", year: "numeric" }
                    )}
                  </Typography>
                </Box>
              </GlassCommentPaper>
            </Box>
          ) : (
            <GlassEmptyState
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: isMobile ? "80px" : "100px",
                py: isMobile ? 2 : 3,
              }}
            >
              <CommentIcon
                sx={{
                  fontSize: isMobile ? "2rem" : "2.5rem",
                  color: alpha(theme.palette.action.disabled, 0.7),
                  mb: 1,
                  filter: `drop-shadow(0 2px 4px ${alpha(
                    theme.palette.action.disabled,
                    0.2
                  )})`,
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{
                  fontSize: isMobile ? "0.875rem" : "0.875rem",
                  fontWeight: 500,
                }}
              >
                No recent activity
              </Typography>
            </GlassEmptyState>
          )}
        </>
      )}
    </GlassActivityCard>
  );
};

export default ActivityCard;
